import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, DollarSign, Send, CheckCircle2, User, Building2, Users, Calendar, Shield, FileText, AlertTriangle, UserPlus, Clock, Edit2, Save, X, XCircle, Trophy, Globe, Download, MapPin, Plus, Trash2, UserCheck, UserX, Hand, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import AdminLayout from '../../components/AdminLayout';
import api from '../../utils/api';
import { toast } from 'sonner';

const CLUB_TYPE_LABELS = {
  yoga_pilates: 'Yoga / Pilates',
  cycling_spin: 'Cycling / Spin',
  hiit_group: 'HIIT / Group Training',
  strength: 'Strength & Conditioning',
  boxing_kickboxing: 'Boxing / Kickboxing Fitness',
  dance_barre: 'Dance / Barre',
  personal_training: 'Personal Training Studio',
  wellness_recovery: 'Wellness / Recovery Studio',
  other: 'Other'
};

const SERVICE_LABELS = {
  group_classes: 'Group Classes',
  personal_training: '1:1 Personal Training',
  open_gym: 'Open Gym',
  youth_classes: 'Youth Classes',
  locker_rooms: 'Locker Rooms / Showers',
  sauna_cold_plunge: 'Sauna / Cold Plunge',
  other: 'Other'
};

const FACILITY_TYPE_LABELS = {
  leased: 'Leased Space',
  owned: 'Owned Building',
  home: 'Home-based',
  mobile: 'Mobile / Pop-up'
};

const AdminApplicationDetail = () => {
  const { id } = useParams();
  const [application, setApplication] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quoteDialogOpen, setQuoteDialogOpen] = useState(false);
  const [sendingQuote, setSendingQuote] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [closedWonDialogOpen, setClosedWonDialogOpen] = useState(false);
  const [closedLostReason, setClosedLostReason] = useState('');
  const [boundPremium, setBoundPremium] = useState('');
  const [dropdownWonOpen, setDropdownWonOpen] = useState(false);
  const [dropdownLostOpen, setDropdownLostOpen] = useState(false);
  const [activityLog, setActivityLog] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();

  const handleDeleteApplication = async () => {
    setDeleting(true);
    try {
      await api.delete(`/applications/${id}`);
      toast.success('Application deleted');
      navigate('/admin/applications');
    } catch (error) {
      toast.error('Failed to delete application');
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  };
  const [riskAssessment, setRiskAssessment] = useState(null);
  
  // Assignment state
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedAssignee, setSelectedAssignee] = useState('');
  const [assigningApp, setAssigningApp] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  
  const [quoteForm, setQuoteForm] = useState({
    premium_amount: '',
    coverage_details: {},
    terms: '',
    valid_days: 30
  });

  // BANT & Notes state
  const [noteText, setNoteText] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [savingBant, setSavingBant] = useState(false);

  const isFitnessFlow = application?.flow_template === 'BOUTIQUE_FITNESS_V1';

  useEffect(() => {
    loadApplicationDetail();
    loadUsers();
    loadCurrentUser();
  }, [id]);

  const loadCurrentUser = () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        // JWT stores user ID in 'sub' field
        setCurrentUser({ user_id: payload.sub });
      } catch (e) {
        console.error('Failed to parse token', e);
      }
    }
  };

  const loadUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data.filter(u => u.active));
    } catch (error) {
      console.error('Failed to load users', error);
    }
  };

  const loadApplicationDetail = async () => {
    try {
      const [appResponse, quotesResponse, activityResponse] = await Promise.all([
        api.get(`/applications/${id}`),
        api.get(`/quotes/application/${id}`),
        api.get(`/applications/${id}/activity`)
      ]);
      setApplication(appResponse.data);
      setEditData(appResponse.data);
      setQuotes(quotesResponse.data);
      setActivityLog(activityResponse.data || []);

      // Fetch linked risk assessment data if available
      if (appResponse.data.risk_assessment_id) {
        try {
          const raRes = await api.get(`/risk-assessment/${appResponse.data.risk_assessment_id}`);
          setRiskAssessment(raRes.data);
        } catch { /* non-blocking */ }
      }
    } catch (error) {
      toast.error('Failed to load application details');
    } finally {
      setLoading(false);
    }
  };

  // Assignment handlers
  const handleClaimApplication = async () => {
    setAssigningApp(true);
    try {
      await api.post(`/applications/${id}/claim`);
      toast.success('Application claimed successfully');
      loadApplicationDetail();
    } catch (error) {
      toast.error('Failed to claim application');
    } finally {
      setAssigningApp(false);
    }
  };

  const handleUnassignApplication = async () => {
    setAssigningApp(true);
    try {
      await api.post(`/applications/${id}/unassign`);
      toast.success('Application returned to queue');
      loadApplicationDetail();
    } catch (error) {
      toast.error('Failed to unassign application');
    } finally {
      setAssigningApp(false);
    }
  };

  const handleAssignApplication = async () => {
    if (!selectedAssignee) return;
    setAssigningApp(true);
    try {
      await api.post(`/applications/${id}/assign?assignee_id=${selectedAssignee}`);
      toast.success('Application assigned successfully');
      setAssignDialogOpen(false);
      setSelectedAssignee('');
      loadApplicationDetail();
    } catch (error) {
      toast.error('Failed to assign application');
    } finally {
      setAssigningApp(false);
    }
  };

  const handleCreateQuote = async (e) => {
    e.preventDefault();
    try {
      await api.post('/quotes', {
        application_id: id,
        premium_amount: parseFloat(quoteForm.premium_amount),
        coverage_details: quoteForm.coverage_details,
        terms: quoteForm.terms,
        valid_days: quoteForm.valid_days
      });
      toast.success('Quote created successfully');
      setQuoteDialogOpen(false);
      setQuoteForm({ premium_amount: '', coverage_details: {}, terms: '', valid_days: 30 });
      loadApplicationDetail();
    } catch (error) {
      toast.error('Failed to create quote');
    }
  };

  const handleSendQuote = async () => {
    if (quotes.length === 0) return;
    setSendingQuote(true);
    try {
      await api.post('/quotes/send', { quote_id: quotes[0].id });
      toast.success('Quote sent to applicant via email');
    } catch (error) {
      toast.error('Failed to send quote email');
    } finally {
      setSendingQuote(false);
    }
  };

  const handleDownloadPdf = async (quoteId) => {
    try {
      const response = await api.get(`/quotes/${quoteId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `quote-${quoteId}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const handleEditChange = (field, value) => {
    setEditData(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveChanges = async () => {
    setSaving(true);
    try {
      await api.put(`/applications/${id}`, editData);
      toast.success('Application updated successfully');
      setApplication(editData);
      setEditMode(false);
    } catch (error) {
      toast.error('Failed to update application');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditData(application);
    setEditMode(false);
  };

  const handleSaveBant = async (field, value) => {
    setSavingBant(true);
    try {
      await api.put(`/applications/${id}`, { [field]: value });
      setApplication(prev => ({ ...prev, [field]: value }));
      toast.success('BANT field updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    } finally {
      setSavingBant(false);
    }
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    setAddingNote(true);
    try {
      const response = await api.post(`/applications/${id}/notes`, { text: noteText });
      setApplication(response.data);
      setNoteText('');
      toast.success('Note added');
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail) {
        toast.error(detail);
      } else {
        toast.error('Failed to add note');
      }
    } finally {
      setAddingNote(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      const updateData = { status: newStatus };
      if (newStatus === 'closed_won' && boundPremium) {
        updateData.bound_premium = parseFloat(boundPremium);
      }
      if (newStatus === 'closed_lost' && closedLostReason) {
        updateData.closed_lost_reason = closedLostReason;
      }
      // Include current BANT data when moving to qualified_opp
      if (newStatus === 'qualified_opp') {
        updateData.bant_timing = application.bant_timing;
        updateData.bant_authority = application.bant_authority;
        updateData.bant_budget = application.bant_budget;
        updateData.bant_contact_name = application.bant_contact_name;
        updateData.bant_contact_email = application.bant_contact_email;
        updateData.bant_contact_phone = application.bant_contact_phone;
      }
      await api.put(`/applications/${id}`, updateData);
      toast.success(`Application marked as ${newStatus.replace('_', ' ')}`);
      setClosedWonDialogOpen(false);
      setStatusDialogOpen(false);
      setDropdownWonOpen(false);
      setDropdownLostOpen(false);
      setBoundPremium('');
      setClosedLostReason('');
      loadApplicationDetail();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail) {
        toast.error(detail);
      } else {
        toast.error('Failed to update status');
      }
    }
  };

  const handleStatusSelect = (newStatus) => {
    if (!newStatus || newStatus === application.status) return;
    if (newStatus === 'closed_won') {
      setBoundPremium(application.bound_premium != null ? String(application.bound_premium) : '');
      setDropdownWonOpen(true);
      return;
    }
    if (newStatus === 'closed_lost') {
      setClosedLostReason('');
      setDropdownLostOpen(true);
      return;
    }
    handleStatusChange(newStatus);
  };

  const describeActivity = (log) => {
    const d = log.details || {};
    switch (log.action) {
      case 'status_changed':
        return `Status changed from "${formatStatus(d.from)}" to "${formatStatus(d.to)}"`;
      case 'application_assigned':
        return `Reassigned from ${d.from || 'Unassigned'} to ${d.to || d.assigned_to_name || 'someone'}`;
      case 'application_claimed':
        return `Claimed — assigned to ${d.to || 'self'}`;
      case 'application_unassigned':
        return 'Unassigned (returned to queue)';
      case 'closed_premium_updated':
        return d.previous != null
          ? `Closed premium updated to $${Number(d.amount).toLocaleString()} (was $${Number(d.previous).toLocaleString()})`
          : `Closed premium set to $${Number(d.amount).toLocaleString()}`;
      default:
        return (log.action || '').replace(/_/g, ' ');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'submitted': return 'bg-[#FFFBEB] text-[#92400E] border border-[#FDE68A]';
      case 'quoted': return 'bg-[#EEF0FF] text-[#6B5CE7] border border-[#C4BFFF]';
      case 'qualified_opp': return 'bg-[#F0FDF4] text-[#166534] border border-[#86EFAC]';
      case 'accepted': case 'bound': case 'closed_won': return 'bg-[#ECFDF5] text-[#065F46] border border-[#A7F3D0]';
      case 'closed_lost': return 'bg-[#FEF2F2] text-[#991B1B] border border-[#FECACA]';
      default: return 'bg-[#F3F4F6] text-[#374151] border border-[#E5E7EB]';
    }
  };

  const formatStatus = (status) => {
    if (status === 'qualified_opp') return 'Qualified Opp';
    if (status === 'closed_won' || status === 'accepted' || status === 'bound') return 'Closed Won';
    if (status === 'closed_lost') return 'Closed Lost';
    return status?.charAt(0).toUpperCase() + status?.slice(1) || 'Unknown';
  };

  const hasClaimsHistory = () => application?.claims_history_flag || application?.has_claims_history || false;
  const isWonStatus = (status) => status === 'closed_won' || status === 'accepted' || status === 'bound';

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#7B6FE8]"></div>
        </div>
      </AdminLayout>
    );
  }

  if (!application) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <p className="text-[#6B7280]">Application not found</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-white border-b border-[#E5E7EB] -mx-8 -mt-8 px-8 pt-6 pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/admin/applications">
                <Button variant="ghost" size="sm" data-testid="back-btn" className="text-[#6B7280] hover:text-[#1A1A1A] hover:bg-[#F3F4F6] -ml-2">
                  <ChevronLeft className="h-5 w-5" />
                </Button>
              </Link>
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-lg font-semibold text-[#1A1A1A]">Application {application.id?.substring(0, 8).toUpperCase()}</h1>
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(application.status)}`}>
                    {formatStatus(application.status)}
                  </span>
                </div>
                <p className="text-sm text-[#6B7280] mt-0.5">{application.org_name}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {!editMode ? (
                <>
                  <Button 
                    data-testid="edit-application-btn"
                    onClick={() => setEditMode(true)}
                    className="bg-[#7B6FE8] text-white hover:bg-[#6B5CE7] font-medium"
                  >
                    <Edit2 className="mr-2 h-4 w-4" />
                    Edit / View
                  </Button>
                  <Button
                    variant="outline"
                    data-testid="delete-application-btn"
                    onClick={() => setDeleteDialogOpen(true)}
                    className="border-[#FECACA] text-[#991B1B] hover:bg-[#FEF2F2]"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete
                  </Button>
                </>
              ) : (
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    onClick={handleCancelEdit}
                    data-testid="cancel-edit-btn"
                    className="border-[#E5E7EB] text-[#1A1A1A] hover:bg-[#F3F4F6]"
                  >
                    <X className="mr-1 h-4 w-4" />
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleSaveChanges}
                    disabled={saving}
                    data-testid="save-changes-btn"
                    className="bg-[#7B6FE8] text-white hover:bg-[#6B5CE7]"
                  >
                    <Save className="mr-1 h-4 w-4" />
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Delete confirmation */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent data-testid="delete-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete this application?</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this application for "{application.org_name}"? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="delete-cancel-btn">Cancel</AlertDialogCancel>
              <AlertDialogAction
                data-testid="delete-confirm-btn"
                onClick={handleDeleteApplication}
                className="bg-[#991B1B] text-white hover:bg-[#7F1D1D]"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white border border-[#E5E7EB] rounded-lg p-5">
            <p className="text-xs text-[#6B7280] font-medium uppercase tracking-wide">Total Premium</p>
            <p className="text-xl font-bold text-[#1A1A1A] mt-2" data-testid="total-premium-value">
              {application.bound_premium != null
                ? `$${application.bound_premium.toLocaleString()}`
                : (quotes.length > 0 && quotes[0].premium_amount ? `$${quotes[0].premium_amount.toLocaleString()}` : 'Pending Quote')}
            </p>
          </div>
          <div className="bg-white border border-[#E5E7EB] rounded-lg p-5">
            <p className="text-xs text-[#6B7280] font-medium uppercase tracking-wide">Coverage Period</p>
            <p className="text-xl font-bold text-[#1A1A1A] mt-2">
              {application.coverage_type === 'event' ? 'Event' : application.coverage_type === 'camp' ? 'Camp' : '12 Month(s)'}
            </p>
          </div>
          <div className="bg-white border border-[#E5E7EB] rounded-lg p-5">
            <p className="text-xs text-[#6B7280] font-medium uppercase tracking-wide">Effective Date</p>
            <p className="text-xl font-bold text-[#1A1A1A] mt-2">
              {application.policy_effective_date ? new Date(application.policy_effective_date).toLocaleDateString() : 'N/A'}
            </p>
          </div>
          <div className="bg-white border border-[#E5E7EB] rounded-lg p-5">
            <p className="text-xs text-[#6B7280] font-medium uppercase tracking-wide">Product/Premium</p>
            <div className="mt-2 space-y-1 text-sm text-[#1A1A1A]">
              <p>General Liability</p>
              <p>Accident and Health</p>
            </div>
          </div>
        </div>

        {/* Risk Assessment Link */}
        {application.risk_assessment_id && (
          <div
            data-testid="linked-risk-assessment"
            className="bg-[#EEF0FF] border border-[#C4BFFF] rounded-lg overflow-hidden"
          >
            <div className="flex items-center gap-3 p-4">
              <Shield className="h-5 w-5 text-[#7B6FE8] shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-[#1A1A1A]">Originated from Risk Assessment</p>
                <p className="text-xs text-[#6B7280]">This application was pre-filled from a completed Risk Assessment report</p>
              </div>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/report/${application.risk_assessment_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-[#7B6FE8] bg-white border border-[#C4BFFF] rounded-md hover:bg-[#F7F6FF] transition-colors shrink-0"
                data-testid="view-assessment-report-btn"
              >
                <ExternalLink className="h-3 w-3" /> View Report
              </a>
            </div>
            {riskAssessment && (
              <div className="border-t border-[#C4BFFF]/40 bg-white/60 px-4 py-3" data-testid="assessment-score-detail">
                <div className="flex items-center gap-6 flex-wrap">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-[#6B7280]">Risk Score</span>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-bold border ${
                      riskAssessment.risk_score >= 80 ? 'bg-[#ECFDF5] text-[#065F46] border-[#A7F3D0]' :
                      riskAssessment.risk_score >= 60 ? 'bg-[#FFFBEB] text-[#92400E] border-[#FDE68A]' :
                      'bg-[#EEF0FF] text-[#7B6FE8] border-[#C4BFFF]'
                    }`} data-testid="assessment-risk-score">
                      {riskAssessment.risk_score}
                    </span>
                  </div>
                  {riskAssessment.safety_score?.percentage !== undefined && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-[#6B7280]">Safety</span>
                      <span className="text-sm font-semibold text-[#1A1A1A]" data-testid="assessment-safety-score">
                        {riskAssessment.safety_score.earned}/{riskAssessment.safety_score.max} ({riskAssessment.safety_score.percentage}%)
                      </span>
                    </div>
                  )}
                  {riskAssessment.insurance_score != null && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-[#6B7280]">Insurance</span>
                      <span className="text-sm font-semibold text-[#1A1A1A]" data-testid="assessment-insurance-score">{riskAssessment.insurance_score}/100</span>
                    </div>
                  )}
                  <span className="text-xs text-[#9CA3AF]">
                    {riskAssessment.assessment_type === 'fitness' ? 'Boutique Fitness' : 'Youth & Amateur Sports'}
                    {riskAssessment.created_at && ` — ${new Date(riskAssessment.created_at).toLocaleDateString()}`}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Main Content - All on One Page */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* Customer Information */}
            <Card className="bg-white border border-[#E5E7EB] shadow-none">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base font-semibold text-[#1A1A1A]">
                  <User className="h-5 w-5 text-[#6B7280]" />
                  Customer Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                {editMode ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-[#1A1A1A] text-sm font-medium">First Name</Label>
                      <Input
                        value={editData.first_name || ''}
                        onChange={(e) => handleEditChange('first_name', e.target.value)}
                        className="mt-1.5 border-[#E5E7EB]"
                      />
                    </div>
                    <div>
                      <Label className="text-[#1A1A1A] text-sm font-medium">Last Name</Label>
                      <Input
                        value={editData.last_name || ''}
                        onChange={(e) => handleEditChange('last_name', e.target.value)}
                        className="mt-1.5 border-[#E5E7EB]"
                      />
                    </div>
                    <div>
                      <Label className="text-[#1A1A1A] text-sm font-medium">Email</Label>
                      <Input
                        type="email"
                        value={editData.applicant_email || ''}
                        onChange={(e) => handleEditChange('applicant_email', e.target.value)}
                        className="mt-1.5 border-[#E5E7EB]"
                      />
                    </div>
                    <div>
                      <Label className="text-[#1A1A1A] text-sm font-medium">Phone</Label>
                      <Input
                        value={editData.applicant_phone || ''}
                        onChange={(e) => handleEditChange('applicant_phone', e.target.value)}
                        className="mt-1.5 border-[#E5E7EB]"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-[#1A1A1A] text-sm font-medium">Organization Name</Label>
                      <Input
                        value={editData.org_name || ''}
                        onChange={(e) => handleEditChange('org_name', e.target.value)}
                        className="mt-1.5 border-[#E5E7EB]"
                      />
                    </div>
                    <div>
                      <Label className="text-[#1A1A1A] text-sm font-medium">Street Address</Label>
                      <Input
                        value={editData.street_address || ''}
                        onChange={(e) => handleEditChange('street_address', e.target.value)}
                        className="mt-1.5 border-[#E5E7EB]"
                      />
                    </div>
                    <div>
                      <Label className="text-[#1A1A1A] text-sm font-medium">City</Label>
                      <Input
                        value={editData.org_city || ''}
                        onChange={(e) => handleEditChange('org_city', e.target.value)}
                        className="mt-1.5 border-[#E5E7EB]"
                      />
                    </div>
                    <div>
                      <Label className="text-[#1A1A1A] text-sm font-medium">State</Label>
                      <Input
                        value={editData.org_state || ''}
                        onChange={(e) => handleEditChange('org_state', e.target.value)}
                        className="mt-1.5 border-[#E5E7EB]"
                      />
                    </div>
                    <div>
                      <Label className="text-[#1A1A1A] text-sm font-medium">Postal Code</Label>
                      <Input
                        value={editData.postal_code || ''}
                        onChange={(e) => handleEditChange('postal_code', e.target.value)}
                        className="mt-1.5 border-[#E5E7EB]"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-xs text-[#6B7280] font-medium">Full Name</p>
                        <p className="text-sm text-[#1A1A1A] font-medium mt-1">{application.first_name} {application.last_name}</p>
                      </div>
                      {application.applicant_title && (
                        <div>
                          <p className="text-xs text-[#6B7280] font-medium">Title</p>
                          <p className="text-sm text-[#1A1A1A] mt-1">{application.applicant_title}</p>
                        </div>
                      )}
                      <div>
                        <p className="text-xs text-[#6B7280] font-medium">Email</p>
                        <a href={`mailto:${application.applicant_email}`} className="text-sm text-[#7B6FE8] hover:underline mt-1 block truncate">
                          {application.applicant_email}
                        </a>
                      </div>
                      <div>
                        <p className="text-xs text-[#6B7280] font-medium">Phone</p>
                        <a href={`tel:${application.applicant_phone}`} className="text-sm text-[#7B6FE8] hover:underline mt-1 block">
                          {application.applicant_phone}
                        </a>
                      </div>
                    </div>
                    <div className="pt-3 border-t border-[#E5E7EB]">
                      <p className="text-xs text-[#6B7280] font-medium">Address</p>
                      <p className="text-sm text-[#1A1A1A] mt-1 flex items-start gap-2">
                        <MapPin className="h-4 w-4 text-[#6B7280] flex-shrink-0 mt-0.5" />
                        {application.street_address}, {application.org_city}, {application.org_state} {application.postal_code}
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Sport Events & Activities */}
            <Card className="bg-white border border-[#E5E7EB] shadow-none">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base font-semibold text-[#1A1A1A]">
                  <Users className="h-5 w-5 text-[#6B7280]" />
                  {isFitnessFlow ? 'Fitness Studio Details' : 'Sport Events & Activities'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {editMode ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {isFitnessFlow ? (
                        <>
                          <div>
                            <Label className="text-[#1A1A1A] text-sm font-medium">Fitness Club Type</Label>
                            <select
                              value={editData.fitness_club_type || ''}
                              onChange={(e) => handleEditChange('fitness_club_type', e.target.value)}
                              className="mt-1.5 w-full h-10 px-3 rounded-md border border-[#E5E7EB] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#7B6FE8] focus:border-transparent"
                            >
                              <option value="">Select Type</option>
                              {Object.entries(CLUB_TYPE_LABELS).map(([value, label]) => (
                                <option key={value} value={value}>{label}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <Label className="text-[#1A1A1A] text-sm font-medium">Location Type</Label>
                            <select
                              value={editData.location_type || 'single'}
                              onChange={(e) => {
                                handleEditChange('location_type', e.target.value);
                                // Initialize locations array if switching to multiple
                                if (e.target.value === 'multiple' && (!editData.locations || editData.locations.length === 0)) {
                                  handleEditChange('locations', [{
                                    name: 'Location 1',
                                    street_address: editData.street_address || '',
                                    city: editData.city || '',
                                    state: editData.state || '',
                                    postal_code: editData.postal_code || ''
                                  }]);
                                }
                              }}
                              className="mt-1.5 w-full h-10 px-3 rounded-md border border-[#E5E7EB] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#7B6FE8] focus:border-transparent"
                            >
                              <option value="single">Single Location</option>
                              <option value="multiple">Multiple Locations</option>
                            </select>
                          </div>
                          <div>
                            <Label className="text-[#1A1A1A] text-sm font-medium">Active Members</Label>
                            <Input
                              type="number"
                              value={editData.active_members || ''}
                              onChange={(e) => handleEditChange('active_members', parseInt(e.target.value) || 0)}
                              className="mt-1.5 border-[#E5E7EB]"
                            />
                          </div>
                          <div>
                            <Label className="text-[#1A1A1A] text-sm font-medium">Daily Check-ins</Label>
                            <Input
                              type="number"
                              value={editData.average_daily_checkins || ''}
                              onChange={(e) => handleEditChange('average_daily_checkins', parseInt(e.target.value) || 0)}
                              className="mt-1.5 border-[#E5E7EB]"
                            />
                          </div>
                          <div>
                            <Label className="text-[#1A1A1A] text-sm font-medium">Instructors/Staff</Label>
                            <Input
                              type="number"
                              value={editData.num_instructors || ''}
                              onChange={(e) => handleEditChange('num_instructors', parseInt(e.target.value) || 0)}
                              className="mt-1.5 border-[#E5E7EB]"
                            />
                          </div>
                          <div>
                            <Label className="text-[#1A1A1A] text-sm font-medium">Square Footage</Label>
                            <Input
                              type="number"
                              value={editData.square_footage || ''}
                              onChange={(e) => handleEditChange('square_footage', parseInt(e.target.value) || 0)}
                              className="mt-1.5 border-[#E5E7EB]"
                            />
                          </div>
                        </>
                      ) : (
                        <>
                          <div>
                            <Label className="text-[#1A1A1A] text-sm font-medium">Participants Under 19</Label>
                            <Input
                              type="number"
                              value={editData.num_participants_under_19 || ''}
                              onChange={(e) => handleEditChange('num_participants_under_19', parseInt(e.target.value) || 0)}
                              className="mt-1.5 border-[#E5E7EB]"
                            />
                          </div>
                          <div>
                            <Label className="text-[#1A1A1A] text-sm font-medium">Participants 19+</Label>
                            <Input
                              type="number"
                              value={editData.num_participants_over_19 || ''}
                              onChange={(e) => handleEditChange('num_participants_over_19', parseInt(e.target.value) || 0)}
                              className="mt-1.5 border-[#E5E7EB]"
                            />
                          </div>
                          <div>
                            <Label className="text-[#1A1A1A] text-sm font-medium">Number of Coaches</Label>
                            <Input
                              type="number"
                              value={editData.num_coaches || ''}
                              onChange={(e) => handleEditChange('num_coaches', parseInt(e.target.value) || 0)}
                              className="mt-1.5 border-[#E5E7EB]"
                            />
                          </div>
                        </>
                      )}
                    </div>
                    
                    {/* Multiple Locations Management */}
                    {isFitnessFlow && editData.location_type === 'multiple' && (
                      <div className="mt-6 pt-4 border-t border-[#E5E7EB]">
                        <div className="flex items-center justify-between mb-4">
                          <Label className="text-[#1A1A1A] text-sm font-semibold">Locations ({(editData.locations || []).length})</Label>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const newLocations = [...(editData.locations || []), {
                                name: `Location ${(editData.locations || []).length + 1}`,
                                street_address: '',
                                city: '',
                                state: '',
                                postal_code: ''
                              }];
                              handleEditChange('locations', newLocations);
                            }}
                            className="text-[#7B6FE8] border-[#7B6FE8] hover:bg-[#EEF0FF]"
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            Add Location
                          </Button>
                        </div>
                        
                        <div className="space-y-4">
                          {(editData.locations || []).map((location, index) => (
                            <div key={index} className="bg-[#F7F8FA] border border-[#E5E7EB] rounded-lg p-4">
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2">
                                  <MapPin className="h-4 w-4 text-[#6B7280]" />
                                  <Input
                                    value={location.name || `Location ${index + 1}`}
                                    onChange={(e) => {
                                      const newLocations = [...editData.locations];
                                      newLocations[index] = { ...newLocations[index], name: e.target.value };
                                      handleEditChange('locations', newLocations);
                                    }}
                                    className="h-8 w-40 text-sm font-medium border-[#E5E7EB]"
                                    placeholder="Location Name"
                                  />
                                </div>
                                {(editData.locations || []).length > 1 && (
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      const newLocations = editData.locations.filter((_, i) => i !== index);
                                      handleEditChange('locations', newLocations);
                                    }}
                                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div className="md:col-span-2">
                                  <Label className="text-xs text-[#6B7280]">Street Address</Label>
                                  <Input
                                    value={location.street_address || ''}
                                    onChange={(e) => {
                                      const newLocations = [...editData.locations];
                                      newLocations[index] = { ...newLocations[index], street_address: e.target.value };
                                      handleEditChange('locations', newLocations);
                                    }}
                                    className="mt-1 h-9 text-sm border-[#E5E7EB]"
                                    placeholder="123 Main St"
                                  />
                                </div>
                                <div>
                                  <Label className="text-xs text-[#6B7280]">City</Label>
                                  <Input
                                    value={location.city || ''}
                                    onChange={(e) => {
                                      const newLocations = [...editData.locations];
                                      newLocations[index] = { ...newLocations[index], city: e.target.value };
                                      handleEditChange('locations', newLocations);
                                    }}
                                    className="mt-1 h-9 text-sm border-[#E5E7EB]"
                                    placeholder="City"
                                  />
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                  <div>
                                    <Label className="text-xs text-[#6B7280]">State</Label>
                                    <Input
                                      value={location.state || ''}
                                      onChange={(e) => {
                                        const newLocations = [...editData.locations];
                                        newLocations[index] = { ...newLocations[index], state: e.target.value };
                                        handleEditChange('locations', newLocations);
                                      }}
                                      className="mt-1 h-9 text-sm border-[#E5E7EB]"
                                      placeholder="TX"
                                    />
                                  </div>
                                  <div>
                                    <Label className="text-xs text-[#6B7280]">ZIP</Label>
                                    <Input
                                      value={location.postal_code || ''}
                                      onChange={(e) => {
                                        const newLocations = [...editData.locations];
                                        newLocations[index] = { ...newLocations[index], postal_code: e.target.value };
                                        handleEditChange('locations', newLocations);
                                      }}
                                      className="mt-1 h-9 text-sm border-[#E5E7EB]"
                                      placeholder="75001"
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="bg-[#F7F8FA] border border-[#E5E7EB] rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <p className="font-semibold text-[#1A1A1A]">
                            {isFitnessFlow 
                              ? (CLUB_TYPE_LABELS[application.fitness_club_type] || 'Fitness Studio')
                              : (application.org_type || 'Sports Organization')}
                          </p>
                          <p className="text-sm text-[#6B7280]">
                            {isFitnessFlow 
                              ? (application.location_type === 'multiple' 
                                  ? `${(application.locations || []).length || 'Multiple'} Locations` 
                                  : 'Single Location')
                              : (application.coverage_type === 'event' ? 'Event Coverage' : 'Annual Policy')}
                          </p>
                        </div>
                        <span className="text-xs px-2.5 py-1 bg-white border border-[#E5E7EB] rounded-full text-[#6B7280]">
                          Class Code: tbd
                        </span>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-3 border-t border-[#E5E7EB]">
                        <div>
                          <p className="text-xs text-[#6B7280]">Location</p>
                          <p className="text-sm font-medium text-[#1A1A1A] mt-0.5">{application.street_address || 'N/A'}</p>
                        </div>
                        {isFitnessFlow ? (
                          <>
                            <div>
                              <p className="text-xs text-[#6B7280]">Active Members</p>
                              <p className="text-sm font-medium text-[#1A1A1A] mt-0.5">{application.active_members?.toLocaleString() || 'N/A'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-[#6B7280]">Daily Check-ins</p>
                              <p className="text-sm font-medium text-[#1A1A1A] mt-0.5">{application.average_daily_checkins?.toLocaleString() || 'N/A'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-[#6B7280]">Staff</p>
                              <p className="text-sm font-medium text-[#1A1A1A] mt-0.5">{application.num_instructors || 'N/A'}</p>
                            </div>
                          </>
                        ) : (
                          <>
                            <div>
                              <p className="text-xs text-[#6B7280]">Participants (Under 19)</p>
                              <p className="text-sm font-medium text-[#1A1A1A] mt-0.5">{application.num_participants_under_19?.toLocaleString() || 'N/A'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-[#6B7280]">Participants (19+)</p>
                              <p className="text-sm font-medium text-[#1A1A1A] mt-0.5">{application.num_participants_over_19?.toLocaleString() || 'N/A'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-[#6B7280]">Staff</p>
                              <p className="text-sm font-medium text-[#1A1A1A] mt-0.5">{application.num_coaches || 'N/A'}</p>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                    
                    {/* Display Sports Breakdown for Sports Applications */}
                    {!isFitnessFlow && application.sports && application.sports.length > 0 && (
                      <div className="space-y-3">
                        <p className="text-sm font-semibold text-[#1A1A1A]">Sports &amp; Participants Breakdown</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                          {application.sports.map((sportItem, index) => (
                            <div key={index} className="bg-white border border-[#E5E7EB] rounded-lg p-3 flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Trophy className="h-4 w-4 text-[#7B6FE8]" />
                                <p className="text-sm font-medium text-[#1A1A1A]">{sportItem.sport || `Sport ${index + 1}`}</p>
                              </div>
                              <div className="flex items-center gap-1.5 bg-[#EEF0FF] px-2.5 py-1 rounded-full">
                                <Users className="h-3.5 w-3.5 text-[#7B6FE8]" />
                                <span className="text-sm font-semibold text-[#6B5CE7]">{sportItem.participants?.toLocaleString() || 0}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className="flex items-center justify-end pt-2 border-t border-[#E5E7EB]">
                          <div className="flex items-center gap-2 text-sm">
                            <span className="text-[#6B7280]">Total Participants:</span>
                            <span className="font-bold text-[#1A1A1A]">
                              {application.sports.reduce((sum, s) => sum + (s.participants || 0), 0).toLocaleString()}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Age Breakdown for Sports Applications */}
                    {!isFitnessFlow && (application.age_12_and_under || application.age_13_to_15 || application.age_16_to_18) && (
                      <div className="space-y-3">
                        <p className="text-sm font-semibold text-[#1A1A1A]">Participant Age Breakdown</p>
                        <div className="grid grid-cols-3 gap-3">
                          <div className="bg-white border border-[#E5E7EB] rounded-lg p-3 text-center">
                            <p className="text-xs text-[#6B7280] mb-1">12 and Under</p>
                            <p className="text-lg font-bold text-[#1A1A1A]">{(application.age_12_and_under || 0).toLocaleString()}</p>
                          </div>
                          <div className="bg-white border border-[#E5E7EB] rounded-lg p-3 text-center">
                            <p className="text-xs text-[#6B7280] mb-1">13 - 15</p>
                            <p className="text-lg font-bold text-[#1A1A1A]">{(application.age_13_to_15 || 0).toLocaleString()}</p>
                          </div>
                          <div className="bg-white border border-[#E5E7EB] rounded-lg p-3 text-center">
                            <p className="text-xs text-[#6B7280] mb-1">16 - 18</p>
                            <p className="text-lg font-bold text-[#1A1A1A]">{(application.age_16_to_18 || 0).toLocaleString()}</p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Display Multiple Locations in View Mode */}
                    {isFitnessFlow && application.location_type === 'multiple' && application.locations && application.locations.length > 0 && (
                      <div className="space-y-3">
                        <p className="text-sm font-semibold text-[#1A1A1A]">All Locations</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {application.locations.map((location, index) => (
                            <div key={index} className="bg-white border border-[#E5E7EB] rounded-lg p-3">
                              <div className="flex items-center gap-2 mb-2">
                                <MapPin className="h-4 w-4 text-[#7B6FE8]" />
                                <p className="text-sm font-medium text-[#1A1A1A]">{location.name || `Location ${index + 1}`}</p>
                              </div>
                              <p className="text-sm text-[#6B7280]">
                                {location.street_address && `${location.street_address}, `}
                                {location.city && `${location.city}, `}
                                {location.state} {location.postal_code}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Coverage Package */}
            <Card className="bg-white border border-[#E5E7EB] shadow-none">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base font-semibold text-[#1A1A1A]">
                  <Shield className="h-5 w-5 text-[#6B7280]" />
                  Coverage Package
                </CardTitle>
              </CardHeader>
              <CardContent>
                {editMode ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-[#1A1A1A] text-sm font-medium">Policy Effective Date</Label>
                      <Input
                        type="date"
                        value={editData.policy_effective_date?.split('T')[0] || ''}
                        onChange={(e) => handleEditChange('policy_effective_date', e.target.value)}
                        className="mt-1.5 border-[#E5E7EB]"
                      />
                    </div>
                    <div>
                      <Label className="text-[#1A1A1A] text-sm font-medium">GL Limit Per Occurrence</Label>
                      <Input
                        value={editData.gl_limit_per_occurrence || ''}
                        onChange={(e) => handleEditChange('gl_limit_per_occurrence', e.target.value)}
                        className="mt-1.5 border-[#E5E7EB]"
                      />
                    </div>
                    <div>
                      <Label className="text-[#1A1A1A] text-sm font-medium">GL Limit Aggregate</Label>
                      <Input
                        value={editData.gl_limit_aggregate || ''}
                        onChange={(e) => handleEditChange('gl_limit_aggregate', e.target.value)}
                        className="mt-1.5 border-[#E5E7EB]"
                      />
                    </div>
                    <div>
                      <Label className="text-[#1A1A1A] text-sm font-medium">Participant Accident Limit</Label>
                      <Input
                        value={editData.participant_accident_limit || ''}
                        onChange={(e) => handleEditChange('participant_accident_limit', e.target.value)}
                        className="mt-1.5 border-[#E5E7EB]"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-[#EEF0FF] rounded-lg p-4 border border-[#C4BFFF]">
                        <p className="text-xs text-[#6B5CE7] font-medium uppercase">Coverage Type</p>
                        <p className="text-lg font-bold text-[#1A1A1A] mt-1">
                          {application.coverage_type === 'event' ? 'Event' : application.coverage_type === 'camp' ? 'Camp' : 'Annual Policy (12-month)'}
                        </p>
                      </div>
                      <div className="bg-[#F7F8FA] rounded-lg p-4 border border-[#E5E7EB]">
                        <p className="text-xs text-[#6B7280] font-medium uppercase">Effective Date</p>
                        <p className="text-lg font-semibold text-[#1A1A1A] mt-1">
                          {application.policy_effective_date ? new Date(application.policy_effective_date).toLocaleDateString() : 'N/A'}
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-3 border-t border-[#E5E7EB]">
                      <div className="bg-[#ECFDF5] rounded-lg p-4 border border-[#A7F3D0]">
                        <p className="text-xs text-[#065F46] font-medium uppercase">Per Occurrence</p>
                        <p className="text-xl font-bold text-[#1A1A1A] mt-1">{application.gl_limit_per_occurrence || 'N/A'}</p>
                      </div>
                      <div className="bg-[#ECFDF5] rounded-lg p-4 border border-[#A7F3D0]">
                        <p className="text-xs text-[#065F46] font-medium uppercase">Aggregate</p>
                        <p className="text-xl font-bold text-[#1A1A1A] mt-1">{application.gl_limit_aggregate || 'N/A'}</p>
                      </div>
                    </div>
                    {application.participant_accident_limit && (
                      <div className="bg-[#F7F8FA] rounded-lg p-4 border border-[#E5E7EB]">
                        <p className="text-xs text-[#6B7280] font-medium uppercase">Participant Accident Limit</p>
                        <p className="text-xl font-bold text-[#1A1A1A] mt-1">{application.participant_accident_limit}</p>
                      </div>
                    )}
                    
                    {/* Other Coverages Requested */}
                    {application.requested_optional_coverages && application.requested_optional_coverages.length > 0 && (
                      <div className="md:col-span-2 bg-[#FFFBEB] rounded-lg p-4 border border-[#FDE68A]">
                        <p className="text-xs text-[#92400E] font-medium uppercase mb-2">Additional Coverages Requested</p>
                        <div className="flex flex-wrap gap-2">
                          {application.requested_optional_coverages.map((coverage, index) => (
                            <Badge key={index} variant="outline" className="bg-white border-[#FDE68A] text-[#92400E]">
                              {coverage}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Underwriting Summary */}
            <Card className="bg-white border border-[#E5E7EB] shadow-none">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base font-semibold text-[#1A1A1A]">
                  <AlertTriangle className="h-5 w-5 text-[#6B7280]" />
                  Underwriting Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className={`flex items-center gap-3 p-3 rounded-lg border ${hasClaimsHistory() ? 'bg-[#FEF2F2] border-[#FECACA]' : 'bg-[#ECFDF5] border-[#A7F3D0]'}`}>
                    <div className="p-2 rounded-full bg-white">
                      {hasClaimsHistory() ? <XCircle className="h-4 w-4 text-[#991B1B]" /> : <CheckCircle2 className="h-4 w-4 text-[#065F46]" />}
                    </div>
                    <div>
                      <p className="text-xs text-[#6B7280]">Prior Claims</p>
                      <p className={`text-sm font-medium ${hasClaimsHistory() ? 'text-[#991B1B]' : 'text-[#065F46]'}`}>
                        {hasClaimsHistory() ? 'Yes - Review Required' : 'No prior claims'}
                      </p>
                    </div>
                  </div>
                  <div className={`flex items-center gap-3 p-3 rounded-lg border ${application.fraud_warranty_accepted ? 'bg-[#ECFDF5] border-[#A7F3D0]' : 'bg-[#FFFBEB] border-[#FDE68A]'}`}>
                    <div className="p-2 rounded-full bg-white">
                      {application.fraud_warranty_accepted ? <CheckCircle2 className="h-4 w-4 text-[#065F46]" /> : <Clock className="h-4 w-4 text-[#92400E]" />}
                    </div>
                    <div>
                      <p className="text-xs text-[#6B7280]">Fraud Warranty</p>
                      <p className={`text-sm font-medium ${application.fraud_warranty_accepted ? 'text-[#065F46]' : 'text-[#92400E]'}`}>
                        {application.fraud_warranty_accepted ? 'Accepted' : 'Pending'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-[#F7F8FA] rounded-lg border border-[#E5E7EB]">
                    <div className="p-2 rounded-full bg-white">
                      <UserPlus className="h-4 w-4 text-[#7B6FE8]" />
                    </div>
                    <div>
                      <p className="text-xs text-[#6B7280]">Additional Insured</p>
                      <p className="text-sm font-medium text-[#1A1A1A]">
                        {application.additional_insured_required ? 'Requested' : 'Not requested'}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Sidebar */}
          <div className="space-y-6">
            {/* Status Management */}
            <Card className="bg-white border border-[#E5E7EB] shadow-none">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold text-[#1A1A1A]">Status Management</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-[#F7F8FA] rounded-lg border border-[#E5E7EB]">
                  <span className="text-sm text-[#6B7280]">Current Status</span>
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(application.status)}`}>
                    {formatStatus(application.status)}
                  </span>
                </div>

                {/* Change Status dropdown (always available) */}
                <div className="space-y-1.5" data-testid="change-status-block">
                  <Label className="text-xs text-[#6B7280] font-medium uppercase tracking-wide">Change Status</Label>
                  <Select value={application.status || 'submitted'} onValueChange={handleStatusSelect}>
                    <SelectTrigger data-testid="status-select-trigger" className="border-[#E5E7EB]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="submitted">Submitted</SelectItem>
                      <SelectItem value="quoted">Quoted</SelectItem>
                      <SelectItem value="qualified_opp">Qualified Opp</SelectItem>
                      <SelectItem value="closed_won" data-testid="status-option-closed-won">Closed Won</SelectItem>
                      <SelectItem value="closed_lost">Closed Lost</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Standalone Closed Won dialog (dropdown / edit triggered) */}
                <Dialog open={dropdownWonOpen} onOpenChange={setDropdownWonOpen}>
                  <DialogContent className="max-w-md">
                    <DialogHeader>
                      <DialogTitle className="text-[#1A1A1A]">Mark as Closed Won</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div>
                        <Label className="text-[#1A1A1A] font-medium">Closed Premium / Final Price ($)</Label>
                        <Input
                          type="number"
                          data-testid="closed-premium-input"
                          value={boundPremium}
                          onChange={(e) => setBoundPremium(e.target.value)}
                          className="mt-1 border-[#E5E7EB]"
                          required
                        />
                      </div>
                      <DialogFooter>
                        <Button onClick={() => handleStatusChange('closed_won')} disabled={!boundPremium} data-testid="confirm-closed-won-btn" className="w-full bg-[#065F46] text-white hover:bg-[#047857]">
                          Confirm Closed Won
                        </Button>
                      </DialogFooter>
                    </div>
                  </DialogContent>
                </Dialog>

                {/* Standalone Closed Lost dialog (dropdown triggered) */}
                <Dialog open={dropdownLostOpen} onOpenChange={setDropdownLostOpen}>
                  <DialogContent className="max-w-md">
                    <DialogHeader>
                      <DialogTitle className="text-[#1A1A1A]">Mark as Closed Lost</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div>
                        <Label className="text-[#1A1A1A] font-medium">Reason for Loss</Label>
                        <Textarea
                          value={closedLostReason}
                          onChange={(e) => setClosedLostReason(e.target.value)}
                          className="mt-1 border-[#E5E7EB]"
                          rows={3}
                          required
                        />
                      </div>
                      <DialogFooter>
                        <Button onClick={() => handleStatusChange('closed_lost')} disabled={!closedLostReason} className="w-full bg-[#991B1B] text-white hover:bg-[#7F1D1D]">
                          Confirm Closed Lost
                        </Button>
                      </DialogFooter>
                    </div>
                  </DialogContent>
                </Dialog>

                {isWonStatus(application.status) && application.bound_premium != null && (
                  <div className="p-3 bg-[#ECFDF5] rounded-lg border border-[#A7F3D0]">
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-[#065F46] font-medium">Closed Premium</p>
                      <Button
                        variant="ghost"
                        size="sm"
                        data-testid="edit-closed-premium-btn"
                        onClick={() => { setBoundPremium(String(application.bound_premium)); setDropdownWonOpen(true); }}
                        className="h-6 px-2 text-[#065F46] hover:bg-[#A7F3D0]/40"
                      >
                        <Edit2 className="h-3 w-3 mr-1" /> Edit
                      </Button>
                    </div>
                    <p className="text-xl font-bold text-[#065F46]" data-testid="closed-premium-value">${application.bound_premium.toLocaleString()}</p>
                  </div>
                )}

                {!isWonStatus(application.status) && application.status !== 'closed_lost' && (
                  <div className="space-y-2">
                    {application.status === 'submitted' && (
                      <Dialog open={quoteDialogOpen} onOpenChange={setQuoteDialogOpen}>
                        <DialogTrigger asChild>
                          <Button className="w-full bg-[#7B6FE8] text-white hover:bg-[#6B5CE7]">
                            <DollarSign className="mr-2 h-4 w-4" />
                            Create Quote
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-md">
                          <DialogHeader>
                            <DialogTitle className="text-[#1A1A1A]">Create Quote</DialogTitle>
                          </DialogHeader>
                          <form onSubmit={handleCreateQuote} className="space-y-4 mt-4">
                            <div>
                              <Label className="text-[#1A1A1A] font-medium">Premium Amount ($)</Label>
                              <Input
                                type="number"
                                min="0"
                                step="0.01"
                                value={quoteForm.premium_amount}
                                onChange={(e) => setQuoteForm({...quoteForm, premium_amount: e.target.value})}
                                required
                                className="mt-1 border-[#E5E7EB]"
                                placeholder="5000"
                              />
                            </div>
                            <div>
                              <Label className="text-[#1A1A1A] font-medium">Terms</Label>
                              <Textarea
                                value={quoteForm.terms}
                                onChange={(e) => setQuoteForm({...quoteForm, terms: e.target.value})}
                                className="mt-1 border-[#E5E7EB]"
                                rows={3}
                              />
                            </div>
                            <DialogFooter>
                              <Button type="submit" className="w-full bg-[#7B6FE8] text-white hover:bg-[#6B5CE7]">
                                Create Quote
                              </Button>
                            </DialogFooter>
                          </form>
                        </DialogContent>
                      </Dialog>
                    )}

                    {application.status === 'quoted' && quotes.length > 0 && (
                      <>
                        <Button onClick={handleSendQuote} disabled={sendingQuote} className="w-full bg-[#7B6FE8] text-white hover:bg-[#6B5CE7]">
                          <Send className="mr-2 h-4 w-4" />
                          {sendingQuote ? 'Sending...' : 'Send Quote Email'}
                        </Button>

                        <Button 
                          onClick={() => handleStatusChange('qualified_opp')}
                          variant="outline"
                          className="w-full border-[#86EFAC] text-[#166534] hover:bg-[#F0FDF4]"
                          data-testid="mark-qualified-opp-btn"
                        >
                          <CheckCircle2 className="mr-2 h-4 w-4" />
                          Mark as Qualified Opp
                        </Button>

                        <Dialog open={closedWonDialogOpen} onOpenChange={setClosedWonDialogOpen}>
                          <DialogTrigger asChild>
                            <Button variant="outline" className="w-full border-[#A7F3D0] text-[#065F46] hover:bg-[#ECFDF5]">
                              <Trophy className="mr-2 h-4 w-4" />
                              Mark as Closed Won
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-md">
                            <DialogHeader>
                              <DialogTitle className="text-[#1A1A1A]">Mark as Closed Won</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4 mt-4">
                              <div>
                                <Label className="text-[#1A1A1A] font-medium">Closed Premium / Final Price ($)</Label>
                                <Input
                                  type="number"
                                  value={boundPremium}
                                  onChange={(e) => setBoundPremium(e.target.value)}
                                  className="mt-1 border-[#E5E7EB]"
                                  required
                                />
                              </div>
                              <DialogFooter>
                                <Button onClick={() => handleStatusChange('closed_won')} disabled={!boundPremium} className="w-full bg-[#065F46] text-white hover:bg-[#047857]">
                                  Confirm Closed Won
                                </Button>
                              </DialogFooter>
                            </div>
                          </DialogContent>
                        </Dialog>

                        <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
                          <DialogTrigger asChild>
                            <Button variant="outline" className="w-full border-[#FECACA] text-[#991B1B] hover:bg-[#FEF2F2]">
                              <XCircle className="mr-2 h-4 w-4" />
                              Mark as Closed Lost
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-md">
                            <DialogHeader>
                              <DialogTitle className="text-[#1A1A1A]">Mark as Closed Lost</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4 mt-4">
                              <div>
                                <Label className="text-[#1A1A1A] font-medium">Reason for Loss</Label>
                                <Textarea
                                  value={closedLostReason}
                                  onChange={(e) => setClosedLostReason(e.target.value)}
                                  className="mt-1 border-[#E5E7EB]"
                                  rows={3}
                                  required
                                />
                              </div>
                              <DialogFooter>
                                <Button onClick={() => handleStatusChange('closed_lost')} disabled={!closedLostReason} className="w-full bg-[#991B1B] text-white hover:bg-[#7F1D1D]">
                                  Confirm Closed Lost
                                </Button>
                              </DialogFooter>
                            </div>
                          </DialogContent>
                        </Dialog>
                      </>
                    )}

                    {application.status === 'qualified_opp' && (
                      <>
                        <div className="p-3 bg-[#F0FDF4] rounded-lg border border-[#86EFAC] mb-2">
                          <p className="text-xs font-medium text-[#166534]">BANT Qualified</p>
                          {application.bant_qualified_at && (
                            <p className="text-xs text-[#166534]/70 mt-0.5">
                              Since {new Date(application.bant_qualified_at).toLocaleDateString()}
                            </p>
                          )}
                        </div>

                        <Dialog open={closedWonDialogOpen} onOpenChange={setClosedWonDialogOpen}>
                          <DialogTrigger asChild>
                            <Button variant="outline" className="w-full border-[#A7F3D0] text-[#065F46] hover:bg-[#ECFDF5]">
                              <Trophy className="mr-2 h-4 w-4" />
                              Mark as Closed Won
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-md">
                            <DialogHeader>
                              <DialogTitle className="text-[#1A1A1A]">Mark as Closed Won</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4 mt-4">
                              <div>
                                <Label className="text-[#1A1A1A] font-medium">Closed Premium / Final Price ($)</Label>
                                <Input
                                  type="number"
                                  value={boundPremium}
                                  onChange={(e) => setBoundPremium(e.target.value)}
                                  className="mt-1 border-[#E5E7EB]"
                                  required
                                />
                              </div>
                              <DialogFooter>
                                <Button onClick={() => handleStatusChange('closed_won')} disabled={!boundPremium} className="w-full bg-[#065F46] text-white hover:bg-[#047857]">
                                  Confirm Closed Won
                                </Button>
                              </DialogFooter>
                            </div>
                          </DialogContent>
                        </Dialog>

                        <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
                          <DialogTrigger asChild>
                            <Button variant="outline" className="w-full border-[#FECACA] text-[#991B1B] hover:bg-[#FEF2F2]">
                              <XCircle className="mr-2 h-4 w-4" />
                              Mark as Closed Lost
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-md">
                            <DialogHeader>
                              <DialogTitle className="text-[#1A1A1A]">Mark as Closed Lost</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4 mt-4">
                              <div>
                                <Label className="text-[#1A1A1A] font-medium">Reason for Loss</Label>
                                <Textarea
                                  value={closedLostReason}
                                  onChange={(e) => setClosedLostReason(e.target.value)}
                                  className="mt-1 border-[#E5E7EB]"
                                  rows={3}
                                  required
                                />
                              </div>
                              <DialogFooter>
                                <Button onClick={() => handleStatusChange('closed_lost')} disabled={!closedLostReason} className="w-full bg-[#991B1B] text-white hover:bg-[#7F1D1D]">
                                  Confirm Closed Lost
                                </Button>
                              </DialogFooter>
                            </div>
                          </DialogContent>
                        </Dialog>
                      </>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Assignment Management */}
            <Card className="bg-white border border-[#E5E7EB] shadow-none">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base font-semibold text-[#1A1A1A]">
                  <UserCheck className="h-5 w-5 text-[#6B7280]" />
                  Assignment
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Current Assignment Status */}
                <div className={`p-3 rounded-lg border ${
                  application.assigned_to_id 
                    ? 'bg-[#ECFDF5] border-[#A7F3D0]' 
                    : 'bg-[#F7F8FA] border-[#E5E7EB]'
                }`}>
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-full ${
                      application.assigned_to_id ? 'bg-white' : 'bg-white'
                    }`}>
                      {application.assigned_to_id ? (
                        <UserCheck className="h-4 w-4 text-[#065F46]" />
                      ) : (
                        <UserX className="h-4 w-4 text-[#9CA3AF]" />
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-[#6B7280]">Assigned To</p>
                      {application.assigned_to_id ? (
                        <>
                          <p className="text-sm font-medium text-[#1A1A1A]">
                            {application.assigned_to_id === currentUser?.user_id 
                              ? 'You' 
                              : application.assigned_to_name}
                          </p>
                          {application.assigned_at && (
                            <p className="text-xs text-[#6B7280] mt-0.5">
                              Since {new Date(application.assigned_at).toLocaleDateString()}
                            </p>
                          )}
                        </>
                      ) : (
                        <p className="text-sm font-medium text-[#6B7280]">Unassigned</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="space-y-2">
                  {!application.assigned_to_id ? (
                    <>
                      <Button 
                        onClick={handleClaimApplication}
                        disabled={assigningApp}
                        data-testid="claim-application-btn"
                        className="w-full bg-[#7B6FE8] text-white hover:bg-[#6B5CE7]"
                      >
                        <Hand className="mr-2 h-4 w-4" />
                        {assigningApp ? 'Claiming...' : 'Claim This Application'}
                      </Button>
                      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
                        <DialogTrigger asChild>
                          <Button variant="outline" className="w-full border-[#E5E7EB] text-[#1A1A1A] hover:bg-[#F3F4F6]">
                            <UserPlus className="mr-2 h-4 w-4" />
                            Assign to Someone
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-md">
                          <DialogHeader>
                            <DialogTitle className="text-[#1A1A1A]">Assign Application</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4 mt-4">
                            <div>
                              <Label className="text-[#1A1A1A] font-medium">Select Team Member</Label>
                              <Select value={selectedAssignee} onValueChange={setSelectedAssignee}>
                                <SelectTrigger className="mt-1.5 border-[#E5E7EB]">
                                  <SelectValue placeholder="Choose a team member" />
                                </SelectTrigger>
                                <SelectContent>
                                  {users.map((user) => (
                                    <SelectItem key={user.id} value={user.id}>
                                      <div className="flex items-center gap-2">
                                        <span>{user.name}</span>
                                        <span className="text-xs text-[#6B7280]">({user.email})</span>
                                      </div>
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <DialogFooter>
                              <Button 
                                onClick={handleAssignApplication} 
                                disabled={!selectedAssignee || assigningApp}
                                className="w-full bg-[#7B6FE8] text-white hover:bg-[#6B5CE7]"
                              >
                                {assigningApp ? 'Assigning...' : 'Assign Application'}
                              </Button>
                            </DialogFooter>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </>
                  ) : application.assigned_to_id === currentUser?.user_id ? (
                    <Button 
                      variant="outline"
                      onClick={handleUnassignApplication}
                      disabled={assigningApp}
                      data-testid="release-application-btn"
                      className="w-full border-[#E5E7EB] text-[#6B7280] hover:bg-[#F3F4F6]"
                    >
                      <UserX className="mr-2 h-4 w-4" />
                      {assigningApp ? 'Releasing...' : 'Release to Queue'}
                    </Button>
                  ) : (
                    <div className="space-y-2">
                      <Button 
                        onClick={handleClaimApplication}
                        disabled={assigningApp}
                        className="w-full bg-[#7B6FE8] text-white hover:bg-[#6B5CE7]"
                      >
                        <Hand className="mr-2 h-4 w-4" />
                        {assigningApp ? 'Reassigning...' : 'Reassign to Me'}
                      </Button>
                      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
                        <DialogTrigger asChild>
                          <Button variant="outline" className="w-full border-[#E5E7EB] text-[#1A1A1A] hover:bg-[#F3F4F6]">
                            <UserPlus className="mr-2 h-4 w-4" />
                            Reassign to Someone Else
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-md">
                          <DialogHeader>
                            <DialogTitle className="text-[#1A1A1A]">Reassign Application</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4 mt-4">
                            <div>
                              <Label className="text-[#1A1A1A] font-medium">Select Team Member</Label>
                              <Select value={selectedAssignee} onValueChange={setSelectedAssignee}>
                                <SelectTrigger className="mt-1.5 border-[#E5E7EB]">
                                  <SelectValue placeholder="Choose a team member" />
                                </SelectTrigger>
                                <SelectContent>
                                  {users.filter(u => u.id !== application.assigned_to_id).map((user) => (
                                    <SelectItem key={user.id} value={user.id}>
                                      <div className="flex items-center gap-2">
                                        <span>{user.name}</span>
                                        <span className="text-xs text-[#6B7280]">({user.email})</span>
                                      </div>
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <DialogFooter>
                              <Button 
                                onClick={handleAssignApplication} 
                                disabled={!selectedAssignee || assigningApp}
                                className="w-full bg-[#7B6FE8] text-white hover:bg-[#6B5CE7]"
                              >
                                {assigningApp ? 'Reassigning...' : 'Reassign Application'}
                              </Button>
                            </DialogFooter>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Activity Log */}
            <Card className="bg-white border border-[#E5E7EB] shadow-none" data-testid="activity-log-section">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold text-[#1A1A1A] flex items-center gap-2">
                  <Clock className="h-5 w-5 text-[#6B7280]" />
                  Activity Log
                  {activityLog.length > 0 && (
                    <Badge variant="secondary" className="ml-auto text-xs">{activityLog.length}</Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {activityLog.length === 0 ? (
                  <p className="text-sm text-[#9CA3AF] text-center py-4" data-testid="activity-log-empty">No status or assignment changes yet.</p>
                ) : (
                  <div className="space-y-3">
                    {activityLog.map((log, i) => (
                      <div key={log.id || i} data-testid={`activity-log-entry-${i}`} className="flex gap-3 pb-3 border-b border-[#F3F4F6] last:border-0 last:pb-0">
                        <div className="mt-1.5 h-2 w-2 rounded-full bg-[#7B6FE8] shrink-0"></div>
                        <div className="min-w-0">
                          <p className="text-sm text-[#1A1A1A]">{describeActivity(log)}</p>
                          <p className="text-xs text-[#6B7280] mt-0.5">
                            {log.user_email || 'System'} · {log.timestamp ? new Date(log.timestamp).toLocaleString() : ''}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* BANT Qualification */}
            <Card className="bg-white border border-[#E5E7EB] shadow-none relative overflow-hidden" data-testid="bant-section">
              <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#6B5CE7] to-[#4ECDC4]"></div>
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold text-[#1A1A1A] flex items-center gap-2">
                  <Shield className="h-5 w-5 text-[#7B6FE8]" />
                  BANT Qualification
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Timing */}
                <div>
                  <Label className="text-xs font-medium text-[#6B7280] uppercase tracking-wider">Timing</Label>
                  <Input
                    data-testid="bant-timing-input"
                    value={application.bant_timing || ''}
                    onChange={(e) => setApplication(prev => ({...prev, bant_timing: e.target.value}))}
                    onBlur={(e) => handleSaveBant('bant_timing', e.target.value)}
                    placeholder="When does the prospect need coverage?"
                    className="mt-1 text-sm border-[#E5E7EB] focus:border-[#7B6FE8]"
                  />
                </div>
                
                {/* Authority */}
                <div>
                  <Label className="text-xs font-medium text-[#6B7280] uppercase tracking-wider">Authority</Label>
                  <Input
                    data-testid="bant-authority-input"
                    value={application.bant_authority || ''}
                    onChange={(e) => setApplication(prev => ({...prev, bant_authority: e.target.value}))}
                    onBlur={(e) => handleSaveBant('bant_authority', e.target.value)}
                    placeholder="Who is the decision maker?"
                    className="mt-1 text-sm border-[#E5E7EB] focus:border-[#7B6FE8]"
                  />
                </div>
                
                {/* Budget */}
                <div>
                  <Label className="text-xs font-medium text-[#6B7280] uppercase tracking-wider">Budget</Label>
                  <Input
                    data-testid="bant-budget-input"
                    value={application.bant_budget || ''}
                    onChange={(e) => setApplication(prev => ({...prev, bant_budget: e.target.value}))}
                    onBlur={(e) => handleSaveBant('bant_budget', e.target.value)}
                    placeholder="What is the budget for coverage?"
                    className="mt-1 text-sm border-[#E5E7EB] focus:border-[#7B6FE8]"
                  />
                </div>

                {/* Contact */}
                <div className="pt-2 border-t border-[#E5E7EB]">
                  <Label className="text-xs font-medium text-[#6B7280] uppercase tracking-wider">Contact</Label>
                  <Input
                    data-testid="bant-contact-name-input"
                    value={application.bant_contact_name || ''}
                    onChange={(e) => setApplication(prev => ({...prev, bant_contact_name: e.target.value}))}
                    onBlur={(e) => handleSaveBant('bant_contact_name', e.target.value)}
                    placeholder="Contact name"
                    className="mt-1 text-sm border-[#E5E7EB] focus:border-[#7B6FE8]"
                  />
                  <Input
                    data-testid="bant-contact-email-input"
                    value={application.bant_contact_email || ''}
                    onChange={(e) => setApplication(prev => ({...prev, bant_contact_email: e.target.value}))}
                    onBlur={(e) => handleSaveBant('bant_contact_email', e.target.value)}
                    placeholder="Contact email"
                    className="mt-1 text-sm border-[#E5E7EB] focus:border-[#7B6FE8]"
                  />
                  <Input
                    data-testid="bant-contact-phone-input"
                    value={application.bant_contact_phone || ''}
                    onChange={(e) => setApplication(prev => ({...prev, bant_contact_phone: e.target.value}))}
                    onBlur={(e) => handleSaveBant('bant_contact_phone', e.target.value)}
                    placeholder="Contact phone"
                    className="mt-1 text-sm border-[#E5E7EB] focus:border-[#7B6FE8]"
                  />
                </div>

                {/* BANT Status Indicator */}
                <div className={`p-2 rounded text-xs font-medium text-center ${
                  application.bant_timing && application.bant_authority && application.bant_budget && (application.bant_contact_name || application.bant_contact_email)
                    ? 'bg-[#ECFDF5] text-[#065F46]'
                    : 'bg-[#FFFBEB] text-[#92400E]'
                }`}>
                  {application.bant_timing && application.bant_authority && application.bant_budget && (application.bant_contact_name || application.bant_contact_email)
                    ? 'All BANT fields complete - ready for qualification'
                    : 'Complete all BANT fields to qualify this opportunity'}
                </div>
              </CardContent>
            </Card>

            {/* Activity Notes */}
            <Card className="bg-white border border-[#E5E7EB] shadow-none" data-testid="notes-section">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold text-[#1A1A1A] flex items-center gap-2">
                  <FileText className="h-5 w-5 text-[#6B7280]" />
                  Notes
                  {application.notes?.length > 0 && (
                    <Badge variant="secondary" className="ml-auto text-xs">{application.notes.length}</Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Add Note */}
                <div>
                  <Textarea
                    data-testid="note-input"
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    placeholder={
                      application.bant_contact_name || application.bant_contact_email
                        ? "Add a note about this account..."
                        : "Add a BANT contact first to enable notes"
                    }
                    className="text-sm border-[#E5E7EB] focus:border-[#7B6FE8] min-h-[80px]"
                    disabled={!application.bant_contact_name && !application.bant_contact_email}
                  />
                  <Button
                    data-testid="add-note-btn"
                    onClick={handleAddNote}
                    disabled={addingNote || !noteText.trim() || (!application.bant_contact_name && !application.bant_contact_email)}
                    className="mt-2 w-full bg-[#7B6FE8] text-white hover:bg-[#6B5CE7] text-sm"
                    size="sm"
                  >
                    {addingNote ? 'Adding...' : 'Add Note'}
                  </Button>
                </div>

                {/* Notes List */}
                {application.notes && application.notes.length > 0 && (
                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {[...application.notes].reverse().map((note) => (
                      <div key={note.id} className="p-3 bg-[#F7F8FA] rounded-lg border border-[#E5E7EB]">
                        <p className="text-sm text-[#1A1A1A] whitespace-pre-wrap break-words" data-testid={`note-text-${note.id}`}>
                          {note.text}
                        </p>
                        <div className="flex items-center gap-2 mt-2 text-xs text-[#9CA3AF]">
                          <span>{note.created_by_name || note.created_by_email}</span>
                          <span>&middot;</span>
                          <span>{new Date(note.created_at).toLocaleString()}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {(!application.notes || application.notes.length === 0) && (
                  <p className="text-sm text-[#9CA3AF] text-center py-3">No notes yet</p>
                )}
              </CardContent>
            </Card>

            {/* Quote History */}
            {quotes.length > 0 && (
              <Card className="bg-white border border-[#E5E7EB] shadow-none">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base font-semibold text-[#1A1A1A]">Quote History</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {quotes.map((quote, idx) => (
                      <div key={quote.id} className={`p-3 rounded-lg border ${idx === 0 ? 'bg-[#EEF0FF] border-[#C4BFFF]' : 'bg-[#F7F8FA] border-[#E5E7EB]'}`}>
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <p className="font-bold text-lg text-[#1A1A1A]">${quote.premium_amount?.toLocaleString()}</p>
                            <p className="text-xs text-[#6B7280]">Created: {new Date(quote.created_at).toLocaleDateString()}</p>
                          </div>
                          {idx === 0 && <Badge className="bg-[#7B6FE8] text-white">Latest</Badge>}
                        </div>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" onClick={() => handleDownloadPdf(quote.id)} className="flex-1 border-[#E5E7EB]">
                            <Download className="mr-1 h-3 w-3" />
                            PDF
                          </Button>
                          <Link to={`/quote/${quote.token}`} target="_blank" className="flex-1">
                            <Button variant="outline" size="sm" className="w-full border-[#E5E7EB]">
                              <Globe className="mr-1 h-3 w-3" />
                              View
                            </Button>
                          </Link>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Timeline */}
            <Card className="bg-white border border-[#E5E7EB] shadow-none">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold text-[#1A1A1A]">Timeline</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-sm">
                    <div className="p-1.5 bg-[#ECFDF5] rounded-full">
                      <FileText className="h-3 w-3 text-[#065F46]" />
                    </div>
                    <div className="flex-1">
                      <p className="text-[#1A1A1A]">Application Submitted</p>
                      <p className="text-xs text-[#6B7280]">{application.submitted_at ? new Date(application.submitted_at).toLocaleString() : 'N/A'}</p>
                    </div>
                  </div>
                  {quotes.length > 0 && (
                    <div className="flex items-center gap-3 text-sm">
                      <div className="p-1.5 bg-[#EEF0FF] rounded-full">
                        <DollarSign className="h-3 w-3 text-[#7B6FE8]" />
                      </div>
                      <div className="flex-1">
                        <p className="text-[#1A1A1A]">Quote Generated</p>
                        <p className="text-xs text-[#6B7280]">{new Date(quotes[0].created_at).toLocaleString()}</p>
                      </div>
                    </div>
                  )}
                  {isWonStatus(application.status) && (
                    <div className="flex items-center gap-3 text-sm">
                      <div className="p-1.5 bg-[#ECFDF5] rounded-full">
                        <Trophy className="h-3 w-3 text-[#065F46]" />
                      </div>
                      <div className="flex-1">
                        <p className="text-[#065F46] font-medium">Closed Won</p>
                        <p className="text-xs text-[#6B7280]">Policy bound successfully</p>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminApplicationDetail;
